export interface LyricLine { time: number; text: string; }
export interface LyricsResult { syncedLyrics?: string; plainLyrics?: string; }
export interface LyricsRecord { id: number; trackName: string; artistName: string; albumName: string; duration: number; instrumental: boolean; plainLyrics?: string | null; syncedLyrics?: string | null; }

const lrclibHeaders = { 'Lrclib-Client': 'JL Music Player/2.0 (lyrics search)' };

export async function fetchLyrics(trackName: string, artistName: string): Promise<LyricsResult | null> {
  const params = new URLSearchParams({ track_name: trackName, artist_name: artistName });
  const response = await fetch(`https://lrclib.net/api/get?${params.toString()}`, { headers: lrclibHeaders });
  if (!response.ok) return null;
  return response.json() as Promise<LyricsResult>;
}

export async function searchLyrics(trackName: string, artistName: string): Promise<LyricsRecord[]> {
  const params = new URLSearchParams({ track_name: trackName, artist_name: artistName });
  const response = await fetch(`https://lrclib.net/api/search?${params.toString()}`, { headers: lrclibHeaders });
  if (!response.ok) throw new Error(response.status === 429 ? 'LRCLIB rate limit reached. Try again shortly.' : 'Lyrics search failed.');
  return response.json() as Promise<LyricsRecord[]>;
}

export function parseSyncedLyrics(input?: string): LyricLine[] {
  if (!input) return [];
  if (/\d{2}:\d{2}:\d{2}[,.]\d{3}\s*-->/.test(input)) return parseSrtLyrics(input);
  const timestampPattern = /\[(\d{1,3}):(\d{2})(?:[.:](\d{1,3}))?\]/g;
  return input.split(/\r?\n/).flatMap(line => {
    const matches = Array.from(line.matchAll(timestampPattern));
    if (!matches.length) return [];
    const text = line.replace(timestampPattern, '').trim();
    if (!text || /^\w+\s*:/i.test(text)) return [];
    return matches.map(match => {
      const fraction = match[3] ? Number(match[3]) / (10 ** match[3].length) : 0;
      return { time: Number(match[1]) * 60 + Number(match[2]) + fraction, text };
    });
  }).sort((a, b) => a.time - b.time);
}

export function parseSrtLyrics(input: string): LyricLine[] {
  const parseTimestamp = (value: string): number | null => {
    const match = value.trim().match(/^(\d+):(\d{2}):(\d{2})[,.](\d{3})$/);
    if (!match) return null;
    return Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3]) + Number(match[4]) / 1000;
  };
  return input.split(/\r?\n\s*\r?\n/).flatMap(block => {
    const lines = block.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
    const timingIndex = lines.findIndex(line => line.includes('-->'));
    if (timingIndex < 0) return [];
    const start = parseTimestamp(lines[timingIndex].split('-->')[0]);
    if (start === null) return [];
    const text = lines.slice(timingIndex + 1).join(' ').trim();
    return text ? [{ time: start, text }] : [];
  }).sort((a, b) => a.time - b.time);
}

export function containsLrcTimestamps(input: string): boolean { return /\[\d{1,3}:\d{2}(?::\d{2})?(?:[.:]\d{1,3})?\]/.test(input); }
export function containsSrtTimestamps(input: string): boolean { return /\d{2}:\d{2}:\d{2}[,.]\d{3}\s*-->\s*\d{2}:\d{2}:\d{2}[,.]\d{3}/.test(input); }
